// SPEC-31: the shell in French, key for key from pt-PT.ts; shell.ts holds
// the shape. Tu form, feminine where PT is, no emoji, sentence case, and
// French typography: a narrow no-break space (U+202F) before ? ! ; : and
// inside « ». The values she chooses (sports, levels, competition types)
// stay PT, read from pt-PT. The founder validates this text before prod.

import type { AssessmentId } from "../schema/types";
import { copy } from "./pt-PT";
import type { ShellCopy } from "./shell";
import { athleteLanguageFr } from "./admin/athleteLanguage";
import { bilanFr } from "./admin/bilan";
import { cycleFr } from "./admin/cycle";
import { bilanFormFr } from "./admin/bilanForm";
import { bodyMapFr } from "./admin/bodyMap";
import { newAccountFr } from "./admin/newAccount";

const ASSISTANT = copy.assistant.name;

/** Values the clinical team reads in one language: never translated. */
const values = copy.profileOnboarding;

/** What a run left mid-way still asks of her, in Conversa's resume. */
function leftToDo(left: number, unit: { one: string; many: string }): string {
  if (left === 0) return "Il ne te reste qu’à confirmer tes réponses. On continue ?";
  if (left === 1) return `Il reste 1 ${unit.one}. On continue ?`;
  return `Il reste ${left} ${unit.many}. On continue ?`;
}

export const fr: ShellCopy = {
  nav: {
    label: "Navigation",
    conversa: "Conversation",
    assessments: "Évaluations",
    profile: "Profil",
    openProfile: "Ouvrir ton profil",
  },
  conversa: {
    loading: "Préparation de la conversation",
    failed: "Quelque chose n’a pas marché de notre côté. Tes réponses sont enregistrées. Réessaie.",
    retry: "Réessayer",
    greetings: { morning: "Bonjour", afternoon: "Bonjour", evening: "Bonsoir" },
    /** "Bonjour, Mariana." or "Bonjour." */
    hello: (greeting: string, firstName: string) => (firstName ? `${greeting}, ${firstName}.` : `${greeting}.`),
    welcome: (count: number, minutes: number) => [
      `Bonjour. Je suis ${ASSISTANT}, je t’accompagne dans tes entraînements.`,
      `Avant de commencer, j’ai besoin de te connaître. Il y a ${count} questions, ça prend ${minutes} minutes.`,
    ],
    resume: (hello: string, left: number, unit: { one: string; many: string }) => [
      `${hello} Tu t’es arrêtée au milieu d’une évaluation.`,
      leftToDo(left, unit),
    ],
    next: (hello: string, remaining: number, minutes: number) => [
      remaining === 1
        ? `${hello} Il reste 1 évaluation pour compléter ton profil de santé.`
        : `${hello} Il reste ${remaining} évaluations pour compléter ton profil de santé.`,
      `La prochaine prend ${minutes} ${minutes === 1 ? "minute" : "minutes"}. On la fait maintenant ?`,
    ],
    allDone: (hello: string) => [
      `${hello} Tu as répondu à toutes les évaluations.`,
      "Il n’y a rien à faire pour l’instant. Quand il y en aura une nouvelle, je te le dirai ici.",
    ],
    thanks: (firstName: string, title: string, assessmentId: AssessmentId, closing?: string) => {
      const merci = firstName ? `Merci, ${firstName}.` : "Merci.";
      if (closing === "sem-sintomas") return [`${merci} J’ai noté que tu ne ressens pas de symptômes au fil du cycle.`];
      const done = `${merci} Tu as terminé « ${title} ».`;
      if (assessmentId === "ciclo-sintomas") return [done, "Je tiendrai compte de ces symptômes en regardant ton cycle et ta charge d’entraînement."];
      if (assessmentId === "ciclo-desporto") return [done, "Je vais rapprocher ces réponses de tes symptômes pour mieux comprendre ton cycle."];
      return [done, "Tes réponses sont gardées en toute confidentialité dans ton profil de santé."];
    },
    units: {
      questions: { one: "question", many: "questions" },
      symptoms: { one: "symptôme", many: "symptômes" },
      parts: { one: "partie", many: "parties" },
    },
    todoKicker: (kicker: string) => `Évaluation · ${kicker}`,
    todoMeta: (count: number, unit: string, minutes: number) => `${count} ${unit} · ${minutes} min · confidentiel`,
    progressKicker: (kicker: string) => `En cours · ${kicker}`,
    progressMeta: (answered: number, total: number) => `${answered} / ${total} réponses · reprends où tu t’es arrêtée`,
    start: "Commencer",
    resumeButton: "Continuer",
    open: "Ouvrir l’évaluation",
    doneTitle: "Tout est à jour",
    doneLine: (done: number) => `${done} évaluations faites`,
    doneCount: (done: number, total: number) => `${done} / ${total}`,
    toAssessments: "Voir mes évaluations",
    composer: `Écris à ${ASSISTANT}…`,
    send: "Envoyer",
  },
  splash: {
    loading: "Ouverture de PYF",
  },
  welcome: {
    title: "Entraîne-toi plus, avec moins de douleur.",
    lead: "PYF suit la douleur, la charge d’entraînement et le cycle, et suggère la prochaine étape.",
    signUp: "Créer un compte",
    signIn: "J’ai déjà un compte · Se connecter",
    language: "Langue",
  },
  signUp: {
    back: "Retour",
    signIn: "Se connecter",
    title: "Crée ton compte.",
    lead: "Ça prend moins d’une minute.",
    firstNameLabel: "Comment tu t’appelles ?",
    firstNamePlaceholder: "Prénom",
    emailLabel: "E-mail",
    emailPlaceholder: "nom@email.com",
    passwordLabel: "Mot de passe",
    passwordPlaceholder: "8 caractères minimum",
    passwordHint: "8 caractères · 1 chiffre",
    submit: "Continuer",
    submitting: "Création…",
    errors: {
      firstNameEmpty: "Dis-moi comment tu t’appelles.",
      firstNameTooLong: "Utilise 40 caractères au maximum.",
      emailInvalid: "Vérifie l’adresse e-mail.",
      emailInUse: "Un compte existe déjà avec cet e-mail.",
      passwordInvalid: "Il faut 8 caractères et 1 chiffre.",
      failed: "Je n’ai pas pu créer le compte. Réessaie.",
    },
    // "En continuant, tu acceptes les Conditions d’utilisation et la
    // Politique de confidentialité. Tes données de santé ne sont qu’à toi.",
    // cut around its two links.
    legal: {
      before: "En continuant, tu acceptes les ",
      terms: "Conditions d’utilisation",
      between: " et la ",
      privacy: "Politique de confidentialité",
      after: ". Tes données de santé ne sont qu’à toi.",
    },
  },
  signIn: {
    back: "Retour",
    signUp: "Créer un compte",
    title: "Te revoilà.",
    lead: "Connecte-toi pour reprendre où tu t’es arrêtée.",
    emailLabel: "E-mail",
    emailPlaceholder: "nom@email.com",
    passwordLabel: "Mot de passe",
    forgot: "J’ai oublié mon mot de passe",
    // "On t’a envoyé un e-mail à {email}.", cut around the address.
    resetSent: { before: "On t’a envoyé un e-mail à ", after: "." },
    submit: "Se connecter",
    submitting: "Connexion…",
    or: "ou",
    google: "Continuer avec Google",
    errors: {
      wrongCredentials: "Mot de passe incorrect. Réessaie.",
      tooManyAttempts: "Trop de tentatives. Réessaie dans 10 minutes.",
      offline: "Pas de connexion. Réessaie.",
      emailFirst: "Écris d’abord ton e-mail.",
      accountExists: "Tu as déjà un compte avec cet e-mail. Connecte-toi avec ton mot de passe.",
      resetFailed: "Je n’ai pas pu envoyer l’e-mail. Réessaie.",
      failed: "Je n’ai pas pu te connecter. Réessaie.",
    },
  },
  newPassword: {
    back: "Retour",
    title: "Choisis un nouveau mot de passe.",
    lead: "Tu t’en serviras pour te connecter.",
    label: "Nouveau mot de passe",
    placeholder: "8 caractères minimum",
    hint: "8 caractères · 1 chiffre",
    submit: "Enregistrer",
    submitting: "Enregistrement…",
    doneTitle: "Mot de passe enregistré.",
    doneLead: "Tu peux te connecter avec ton nouveau mot de passe.",
    invalidTitle: "Ce lien ne marche plus.",
    invalidLead: "Chaque lien dure 1 heure et ne sert qu’une fois. Demandes-en un autre sur l’écran « Se connecter ».",
    signIn: "Se connecter",
    errors: {
      passwordInvalid: "Il faut 8 caractères et 1 chiffre.",
      offline: "Pas de connexion. Réessaie.",
      failed: "Je n’ai pas pu enregistrer le mot de passe. Réessaie.",
    },
  },
  profileOnboarding: {
    back: "Retour",
    part: "Partie 1 · Données générales",
    typing: "Lena est en train d’écrire",
    send: "Envoyer",
    greeting: "Bonjour. Je suis Lena, je t’accompagne dans tes entraînements.",
    firstName: ["Avant de commencer, j’ai besoin de te connaître. Comment tu t’appelles ?"],
    firstNamePlaceholder: "Ton prénom",
    birthDate: (name: string) => [`Enchantée, ${name}. Quelle est ta date de naissance ?`],
    birthDateAgain: "Quelle est ta date de naissance ?",
    birthDatePlaceholder: "jj/mm/aaaa",
    dateUnreadable: "Je n’ai pas réussi à lire la date. Écris-la comme ça : 14/03/2001.",
    tooYoung: (name: string, minAge: number) => [
      `Merci, ${name}. PYF s’adresse aux athlètes à partir de ${minAge} ans.`,
      `Alors on s’arrête là. Je n’ai pas enregistré ta date de naissance. Quand tu auras ${minAge} ans, je t’attendrai ici.`,
    ],
    leave: "Quitter",
    modality: (age: number) => [`Tu as ${age} ans.`, "Quel est ton sport ?"],
    modalityChips: values.modalityChips,
    modalityOther: "Autre",
    modalityOtherPlaceholder: "Écris ton sport",
    orWriteHere: "Ou écris ici…",
    position: ["Quel est ton poste, ta discipline ou ta catégorie de poids ?"],
    positionPlaceholder: "Ex. : milieu, 400 m, −60 kg",
    level: ["À quel niveau tu fais de la compétition ?"],
    levelChips: values.levelChips,
    levelOther: "Autre",
    levelOtherPlaceholder: "Écris ton niveau",
    club: ["Dans quel club tu t’entraînes ?"],
    noClub: "Sans club",
    clubPlaceholder: "Nom du club",
    load: ["Tu fais combien d’entraînements par semaine, et combien d’heures en tout ?"],
    trainings: "Entraînements par semaine",
    hours: "Heures par semaine",
    less: "Moins",
    more: "Plus",
    confirm: "Confirmer",
    // The card: her load is a value, kept in PT like the sport and the level (« 4 treinos · 6 h por semana »).
    loadAnswer: values.loadAnswer,
    competition: ["À quoi ressemble ta compétition ?"],
    competitionOptions: {
      league: { letter: "A", title: values.competitionOptions.league.title, hint: "Sports collectifs" },
      events: { letter: "B", title: values.competitionOptions.events.title, hint: "Sports individuels ou de combat" },
    },
    closing: (name: string) => [
      `Merci, ${name}. La partie 1 est faite.`,
      "Voici le résumé. Tu peux corriger n’importe quelle donnée plus tard dans ton profil.",
    ],
    saveFailed: "Je n’ai pas pu enregistrer tes données. Vérifie ta connexion et réessaie.",
    retry: "Réessayer",
    summary: {
      title: "Partie 1 terminée",
      name: "Prénom",
      birth: "Naissance",
      birthValue: (date: string, age: number) => `${date} · ${age} ans`,
      modality: "Sport",
      position: "Poste",
      laterality: "Latéralité",
      level: "Niveau",
      club: "Club",
      load: "Charge",
      competition: "Compétition",
    },
    toAssessments: "Voir mes évaluations",
    toConversa: "Retour à la conversation",
    toProfile: "Retour au profil",
    leaveEdit: "Quitter la modification",
  },
  assessments: {
    today: (weekday: string, day: number, month: string) => `Aujourd’hui · ${weekday} ${day} ${month}`,
    // Sunday first, as Date.getDay() counts.
    weekdays: ["dim", "lun", "mar", "mer", "jeu", "ven", "sam"],
    months: ["janv", "févr", "mars", "avr", "mai", "juin", "juil", "août", "sept", "oct", "nov", "déc"],
    title: "Tes évaluations.",
    loading: "Chargement de tes évaluations",
    offline: "Pas de connexion",
    failed: "Quelque chose n’a pas marché de notre côté. Tes réponses sont enregistrées. Réessaie.",
    retry: "Réessayer",
    completeProfile: "Complète tes données générales",
    profileLine: (age: number, modality: string, position: string) => `${age} ans · ${modality} · ${position}`,
    doneLabel: "Terminées",
    left: (remaining: number, minutes: number) =>
      remaining === 1
        ? `Il reste 1 évaluation · environ ${minutes} min`
        : `Il reste ${remaining} évaluations · environ ${minutes} min`,
    allDone: "Toutes les évaluations sont faites.",
    groups: { progress: "En cours", todo: "À faire", done: "Terminées" },
    tagTodo: "À faire",
    tagDone: "✓ Fait",
    tagProgress: (answered: number, total: number) => `${answered} / ${total}`,
    units: { questions: "questions", symptoms: "symptômes", parts: "parties" },
    todoMeta: (kicker: string, count: number, unit: string, minutes: number) => `${kicker} · ${count} ${unit} · ${minutes} min`,
    progressMeta: (kicker: string) => `${kicker} · en cours`,
    doneMeta: (kicker: string, day: number, month: string, editable: boolean) =>
      `${kicker} · fait le ${day} ${month} · ${editable ? "modifiable" : "lecture seule"}`,
    // The letters and the instruments' kickers stay as in PT; only
    // « Perfil » and « Ciclo menstrual » are words to translate.
    rows: {
      dados: { letter: "P", title: "Données générales", kicker: "Profil" },
      iciq: { letter: "I", title: "Fuites urinaires", kicker: "ICIQ-UI SF" },
      "ciclo-sintomas": { letter: "C", title: "Ton cycle : symptômes", kicker: "Cycle menstruel" },
      "ciclo-desporto": { letter: "C", title: "Le cycle et le sport", kicker: "Cycle menstruel" },
      leafq: { letter: "L", title: "Blessures, digestion et cycle", kicker: "LEAF-Q" },
      sono: { letter: "S", title: "Sommeil", kicker: "ASBQ" },
      beda: { letter: "B", title: "Alimentation et image du corps", kicker: "BEDA-Q" },
      ciss: { letter: "O", title: "Yeux et vision", kicker: "CISS" },
      fonseca: { letter: "M", title: "Mâchoire et mastication", kicker: "Fonseca" },
    },
  },
  profile: {
    title: "Profil",
    loading: "Chargement de ton profil",
    generalData: "Données générales",
    edit: "Modifier",
    answer: "Répondre",
    notAnswered: "Tu n’as pas encore répondu.",
    rows: {
      birth: "Date de naissance",
      modality: "Sport",
      position: "Poste",
      laterality: "Latéralité",
      level: "Niveau",
      club: "Club",
      load: "Charge",
      competition: "Compétition",
    },
    empty: "—",
    privacyTitle: "Tes données de santé sont confidentielles",
    privacyText: "Elles ne sont partagées ni avec ton club ni avec l’équipe qui t’entraîne.",
    signOut: "Se déconnecter",
    sending: "Envoi de tes réponses…",
    confirmTitle: "Se déconnecter ?",
    unsent: "Certaines réponses n’ont pas encore été envoyées. Si tu te déconnectes maintenant, tu les perds.",
    confirm: "Se déconnecter",
    cancel: "Annuler",
    leaving: "Déconnexion…",
    signOutFailed: "Je n’ai pas pu te déconnecter. Réessaie.",
    language: {
      title: "Langue",
      line: "S’applique à toute l’app.",
      saveFailed: "Je n’ai pas pu enregistrer la langue dans ton compte. Elle reste sur ce téléphone et je réessaie.",
    },
  },
  legalNotice: {
    onlyPortuguese: "Ce texte n’existe pour l’instant qu’en portugais.",
  },
  placeholder: {
    soon: "Cet écran est encore en construction.",
    back: "Retour aux évaluations",
  },
  // SPEC-38: the PYF team's admin (SPEC-33, SPEC-34), tu form as in PT,
  // feminine where PT is (the athletes). The screen sets the labels in capitals.
  admin: {
    language: "Langue",
    skip: "Aller à la base",
    label: "Admin · données de santé confidentielles",
    sections: "Sections de l’admin",
    base: "Base de données",
    profile: "Profil",
    toApp: "App",
    title: "Base des utilisatrices.",
    signInLead: "Connecte-toi avec ton compte de l’équipe PYF.",
    denied: "Tu n’as pas accès à cet espace.",
    signOut: "Se déconnecter",
    signOutFailed: "Je n’ai pas pu te déconnecter. Réessaie.",
    loading: "Chargement de la base…",
    failed: "Je n’ai pas pu charger la base. Réessaie.",
    retry: "Réessayer",
    searchLabel: "Rechercher des utilisatrices",
    // The search reads the first name: « nom » would read as the surname.
    search: "Rechercher par prénom, e-mail ou club…",
    export: "Exporter en CSV",
    exporting: "Export en cours…",
    exportFailed: "Je n’ai pas pu exporter. Réessaie.",
    stats: {
      accounts: "Utilisatrices",
      newAccounts: (n: number) => (n === 1 ? "1 nouvelle ces 30 derniers jours" : `${n} nouvelles ces 30 derniers jours`),
      done: "Évaluations terminées",
      possible: (possible: number) => `/ ${possible}`,
      /** A narrow no-break space before the percent sign, as French writes it. */
      donePercent: (percent: number) => `${percent} % du total possible`,
      inProgress: "En cours",
      halfway: (n: number) => (n === 1 ? "laissée à mi-chemin" : "laissées à mi-chemin"),
      alerts: "Avec alerte",
      aboveThreshold: "résultat au-dessus du seuil",
    },
    count: (n: number) => (n === 1 ? "1 utilisatrice" : `${n} utilisatrices`),
    filtersLabel: "Filtrer les utilisatrices",
    filters: { all: "Toutes", alert: "Avec alerte", progress: "En cours", inactive: "Inactives" },
    caption: "Utilisatrices de PYF",
    columns: {
      user: "Utilisatrice",
      club: "Club · sport",
      level: "Niveau",
      assessments: "Évaluations",
      dates: "Créée · dernière activité",
      status: "Statut",
    },
    none: "—",
    noClub: "Sans club",
    detail: (parts: readonly string[]) => parts.join(" · "),
    years: (age: number) => `${age} ans`,
    of: (total: number) => `/ ${total}`,
    date: (day: number, month: string) => `${day} ${month}`,
    // « Complet » is the profile's, masculine as PT's « Completo »; the others are hers.
    statuses: { alert: "Alerte", inactive: "Inactive", progress: "En cours", complete: "✓ Complet", active: "Active" },
    empty: "Aucune utilisatrice ne correspond.",
    more: "Afficher plus",
    athlete: {
      skip: "Aller au profil",
      back: "Base des utilisatrices",
      loading: "Chargement du profil…",
      failed: "Je n’ai pas pu charger le profil. Réessaie.",
      notFound: "Cette utilisatrice n’existe pas.",
      meta: (parts: readonly string[]) => parts.join(" · "),
      created: (date: string) => `compte créé le ${date}`,
      activity: (date: string) => `dernière activité le ${date}`,
      export: "Exporter les réponses",
      exporting: "Export en cours…",
      exportFailed: "Je n’ai pas pu exporter. Réessaie.",
      stats: {
        done: "Évaluations terminées",
        toDo: (n: number) => (n === 0 ? "profil de santé complet" : `${n} à faire`),
        inProgress: "En cours",
        halfway: (n: number) => {
          if (n === 0) return "aucune à mi-chemin";
          if (n === 1) return "laissée à mi-chemin";
          return "laissées à mi-chemin";
        },
        alerts: "Alertes",
        instruments: (kickers: readonly string[]) => kickers.join(" · "),
        noAlert: "aucun résultat au-dessus du seuil",
        since: "Depuis l’inscription",
        days: (n: number) => (n === 1 ? "jour" : "jours"),
        lastActivity: (days: number) => {
          if (days === 0) return "dernière activité aujourd’hui";
          if (days === 1) return "dernière activité il y a 1 jour";
          return `dernière activité il y a ${days} jours`;
        },
      },
      generalData: "Données générales",
      notAnswered: "Elle n’a pas encore répondu.",
      privacyTitle: "Données de santé confidentielles",
      // « l’équipe qui l’entraîne » as the athlete app says it (profile.privacyText).
      privacyText: "Visibles uniquement par l’équipe clinique. Jamais partagées avec son club ni avec l’équipe qui l’entraîne.",
      assessments: "Évaluations et résultats",
      doneMeta: (kicker: string, date: string) => `${kicker} · fait le ${date}`,
      progressMeta: (kicker: string, answered: number, total: number) => `${kicker} · ${answered} / ${total} réponses`,
      todoMeta: (kicker: string) => `${kicker} · à faire`,
      tags: { done: "✓ Fait", progress: "En cours", todo: "À faire", alert: "Alerte" },
      max: (max: number) => `/${max}`,
      result: (score: number, max: number, threshold: number | undefined, alert: boolean) => {
        const parts = [`Résultat ${score} / ${max}`];
        if (threshold !== undefined) parts.push(`seuil ${threshold}`);
        if (alert) parts.push("au-dessus du seuil");
        return parts.join(" · ");
      },
      noScore: "Pas de score",
    },
    // SPEC-44 to SPEC-51: the bilan presencial, its body map and its form sections.
    bilan: bilanFr,
    cycle: cycleFr,
    bodyMap: bodyMapFr,
    bilanForm: bilanFormFr,
    athleteLanguage: athleteLanguageFr,
    newAccount: newAccountFr,
  },
};
